import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'

import { IQuestionComponentProps, IQuestionComponentRef, QuestionViewFrame } from '../types.ts'
import {
  EcommerceQuestionModelTypes,
  EcommerceSubmitResponse,
  EcommerceResponse,
  IEcommerceResponseValidation
} from './types.ts'

import useViewport from '../../modules/viewport/useViewport.ts'
import useForceImmutable from '@aytm/lib/hooks/useForceImmutable'
import useSession from '../../session/useSession.ts'
import useTimeline from '../../modules/timeline/useTimeline.ts'
import useTheme from '@aytm/app/swidget/base/modules/theme/useTheme'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'

import QuestionFrame from '../_common/QuestionFrame/QuestionFrame.tsx'
import { getMediumAssetPath, getOldMediumAssetPath } from '@aytm/lib/common/assets'
import { layoutIsCompact } from '../../lib/layout.ts'
import TextWithOptions from '../../modules/preview/components/TextWithOptions.tsx'
import usePreviewOptions from '../../modules/preview/usePreviewOptions.ts'
import ECommerceView from './Ecommerce/ECommerceView.tsx'
import AmazonView from './Amazon/AmazonView.tsx'
import WalmartView from './Walmart/WalmartView.tsx'
import TargetView from './Target/TargetView.tsx'
import NikeView from './Nike/NikeView.tsx'
import ClosedShelfView from './View.tsx'
import IntroShelfView from './IntroView.tsx'
import StandartView from './Standart/StandartView.tsx'
import CostcoView from './Costco/CostcoView.tsx'
import ChewyView from './Chewy/ChewyView.tsx'
import KrogerView from './Kroger/KrogerView.tsx'
import SamsClubView from './SamsClub/SamsClubView.tsx'
import textWithOptionsOld from '../../modules/preview/functions/textWithOptionsOld.ts'

/* eslint-disable @typescript-eslint/no-unsafe-assignment,
@typescript-eslint/no-explicit-any,
@typescript-eslint/no-unsafe-member-access,
@typescript-eslint/no-unsafe-call,
@typescript-eslint/no-var-requires */
const SH = require('../../../questions/components/sh')
const OldEcommerceQuestion: any = SH.default

const EcommerceQuestion = React.memo(
  React.forwardRef<IQuestionComponentRef, IQuestionComponentProps<EcommerceQuestionModelTypes>>(({ model }, ref) => {
    const [[response, responseValidation, __frame], setResponse] = useState<
      [EcommerceResponse, IEcommerceResponseValidation, QuestionViewFrame]
    >([model.response, model.responseValidation, model.frame])

    const session = useSession()
    const viewport = useViewport()
    const timeline = useTimeline()
    const theme = useTheme()
    const { t } = useSurveyLocale()
    const { localeID } = useSurveyLocale()
    const previewOptions = usePreviewOptions()
    const [closed, setClosed] = useState(false)
    const [intro, setIntro] = useState(false)
    const findability =
      'find' == model.schema.mode_type || (1 == model.schema.max_cart_size && !model.schema.shopping_cart)

    const [showValidation, setShowValidation] = useState(false)
    const [width, setWidth] = useState(viewport.mobile ? Math.min(viewport.width, 600) : 600)
    const [widgetWidth, setWidgetWidth] = useState<number>(null)

    const parentRef = useRef(null)

    const doNext = () => {
      session.enableNext(model.responseValidation?.valid)
      session.doNext()
    }

    useEffect(() => {
      session.enableNext(model.responseValidation?.valid || intro)
    }, [session, model.responseValidation?.valid, model.frame, intro])

    useEffect(() => {
      setWidth(viewport.mobile ? Math.min(viewport.width, widgetWidth || viewport.width) : widgetWidth || 600)
    }, [viewport.width, viewport.mobile, widgetWidth])

    const schema = useRef(null)
    if (!schema.current)
      schema.current = (() => {
        const sc = { ...model.schema }
        setIntro(true)
        model.schema.answers.forEach((answer, index) => {
          answer.index = index
          if (answer.image) {
            answer.image.trimmed_path = getOldMediumAssetPath(answer.image)
          }
          if (answer.gallery) answer.gallery.forEach(image => (image.trimmed_path = getOldMediumAssetPath(image)))
          answer.available = true
        })
        const questionText = textWithOptionsOld(
          model.schema.text,
          previewOptions,
          session.rules?.sub?.[model.schema.id],
          session.rules?.hide_text?.[model.schema.id]
        )
        // the schema permits a nil instruction; interpolating one renders a literal 'null' in the hint
        sc.instruction = model.schema.instruction ? `${questionText}<br/>${model.schema.instruction}` : questionText

        return sc
      })()

    function keyDownHandler(event: globalThis.KeyboardEvent) {
      if ('Escape' === event.key) {
        event.preventDefault()
        setClosed(true)
      }
    }
    useEffect(() => {
      if (session.action.preview) document.addEventListener('keydown', keyDownHandler)

      return () => {
        document.removeEventListener('keydown', keyDownHandler)
      }
    }, [session.action.preview])

    const changeWidth = (width: number) => {
      session.setWidgetWidth(width)
      setWidgetWidth(width)
    }

    const questionTitle = (
      <>
        {previewOptions.showNumbers ? (
          <span data-testid="SW-Question-ID">{model.schema.id.toUpperCase()}. </span>
        ) : null}
        {previewOptions.showHidden || !session.rules?.hide_text?.[model.schema.id]?.condition ? (
          <TextWithOptions
            text={model.schema.text}
            options={previewOptions}
            rules={session.rules?.sub?.[model.schema.id]?.[model.schema.id]}
          />
        ) : null}
      </>
    )

    const logEvent = (ev: any) => {
      timeline.log({ type: ev.type, ref: ev.ref, target: ev.target })
    }

    const onUpdateResponse = (response: EcommerceResponse) => {
      model.response = { ...response }
      setResponse([model.response, model.responseValidation, model.frame])
      if (findability && model.responseValidation.valid) {
        doNext()
        return
      }
      setShowValidation(false)
    }

    const onUpdateOldResponse = (answer: any, validation: any) => {
      model.responseValidation.valid = validation.get('valid')
      model.response = {
        answers: answer.get('answers').reduce((prev: EcommerceSubmitResponse, cur: any) => {
          if (cur.get('count')) prev.push({ id: cur.get('id'), v: cur.get('count') })
          return prev
        }, [])
      }
      setResponse([model.response, model.responseValidation, model.frame])
    }

    useImperativeHandle<IQuestionComponentRef, IQuestionComponentRef>(ref, () => {
      return {
        tryNext() {
          if (intro) {
            session.progress.advanceQuestionSeconds(model.questionTime * 0.3)
            session.enableNext(false)
            setIntro(false)
            return true
          } else if (model.responseValidation?.valid || session.skipValidation) {
            return false
          } else {
            session.enableNext(false)
            return true
          }
        },
        tryPrev() {
          return false
        },
        showValidation() {
          setShowValidation(true)
        }
      }
    }, [intro, model.responseValidation?.valid, model.questionTime, session])

    const checkout = () => {
      session.doNext()
    }

    const onShown = useCallback(() => {
      session.enableNext(true)
    }, [session])

    const immutableModelSchema = useForceImmutable(schema.current)

    const imageRef = useRef<string[]>(null)
    if (!imageRef.current)
      imageRef.current = model.schema.answers.reduce((array: string[], answer) => {
        if (answer.image) array.push(getMediumAssetPath(answer.image, 'transparent'))
        return array
      }, [])

    const onSimulationClose = () => {
      timeline.log({
        type: 'close_simulation'
      })
      setClosed(true)
    }

    const onSimulationOpen = () => {
      timeline.log({
        type: 'open_simulation'
      })
      setClosed(false)
    }

    if (intro)
      return (
        <QuestionFrame model={model} noImage={true} titleText={t('questions', 'ec', 'introHeader')}>
          <IntroShelfView
            store={schema.current.store}
            findability={findability}
            onShown={onShown}
            storeName={'ecommercew' === schema.current.store ? schema.current.name : null}
          />
        </QuestionFrame>
      )

    if ('ecommerce' == schema.current.store)
      return (
        <QuestionFrame model={model} fullsize={true} images={imageRef.current}>
          <ECommerceView
            schema={schema.current}
            onUpdateResponse={onUpdateResponse}
            response={response}
            responseValidation={responseValidation}
            showValidation={showValidation}
          />
        </QuestionFrame>
      )

    if ('amazonw' == schema.current.store)
      return (
        <QuestionFrame model={model} fullsize={true} images={imageRef.current} noTitle={true}>
          <div ref={parentRef}>
            {closed ? (
              <ClosedShelfView
                schema={schema.current}
                response={response}
                store="Amazon"
                onAction={onSimulationOpen}
                onUpdateResponse={onUpdateResponse}
              />
            ) : (
              <AmazonView
                schema={schema.current}
                onUpdateResponse={onUpdateResponse}
                response={response}
                checkout={checkout}
                responseValidation={responseValidation}
                showValidation={showValidation}
                setClosed={onSimulationClose}
                parentRef={parentRef}
              />
            )}
          </div>
        </QuestionFrame>
      )

    if ('targetw' == schema.current.store)
      return (
        <QuestionFrame model={model} fullsize={true} images={imageRef.current} noTitle={true}>
          <div ref={parentRef}>
            {closed ? (
              <ClosedShelfView
                schema={schema.current}
                response={response}
                store="Target"
                onAction={onSimulationOpen}
                onUpdateResponse={onUpdateResponse}
              />
            ) : (
              <TargetView
                schema={schema.current}
                onUpdateResponse={onUpdateResponse}
                response={response}
                checkout={checkout}
                responseValidation={responseValidation}
                showValidation={showValidation}
                setClosed={onSimulationClose}
                parentRef={parentRef}
              />
            )}
          </div>
        </QuestionFrame>
      )

    if ('walmartw' == schema.current.store)
      return (
        <QuestionFrame model={model} fullsize={true} images={imageRef.current} noTitle={true}>
          <div ref={parentRef}>
            {closed ? (
              <ClosedShelfView
                schema={schema.current}
                response={response}
                store="Walmart"
                onAction={onSimulationOpen}
                onUpdateResponse={onUpdateResponse}
              />
            ) : (
              <WalmartView
                schema={schema.current}
                onUpdateResponse={onUpdateResponse}
                response={response}
                checkout={checkout}
                responseValidation={responseValidation}
                showValidation={showValidation}
                setClosed={onSimulationClose}
                parentRef={parentRef}
              />
            )}
          </div>
        </QuestionFrame>
      )

    if ('nikew' == schema.current.store)
      return (
        <QuestionFrame model={model} fullsize={true} images={imageRef.current} noTitle={true}>
          <div ref={parentRef}>
            {closed ? (
              <ClosedShelfView
                schema={schema.current}
                response={response}
                store="Nike"
                onAction={onSimulationOpen}
                onUpdateResponse={onUpdateResponse}
              />
            ) : (
              <NikeView
                schema={schema.current}
                onUpdateResponse={onUpdateResponse}
                response={response}
                checkout={checkout}
                responseValidation={responseValidation}
                showValidation={showValidation}
                setClosed={onSimulationClose}
                parentRef={parentRef}
              />
            )}
          </div>
        </QuestionFrame>
      )

    if ('ecommercew' == schema.current.store)
      return (
        <QuestionFrame model={model} fullsize={true} images={imageRef.current} noTitle={true} noImage={true}>
          <div ref={parentRef}>
            {closed ? (
              <ClosedShelfView
                schema={schema.current}
                response={response}
                store="Ecommerce"
                onAction={onSimulationOpen}
                onUpdateResponse={onUpdateResponse}
              />
            ) : (
              <StandartView
                schema={schema.current}
                onUpdateResponse={onUpdateResponse}
                response={response}
                checkout={checkout}
                responseValidation={responseValidation}
                showValidation={showValidation}
                setClosed={onSimulationClose}
                parentRef={parentRef}
              />
            )}
          </div>
        </QuestionFrame>
      )

    if ('costcow' == schema.current.store)
      return (
        <QuestionFrame model={model} fullsize={true} images={imageRef.current} noTitle={true} noImage={true}>
          <div ref={parentRef}>
            {closed ? (
              <ClosedShelfView
                schema={schema.current}
                response={response}
                store="Costco"
                onAction={onSimulationOpen}
                onUpdateResponse={onUpdateResponse}
              />
            ) : (
              <CostcoView
                schema={schema.current}
                onUpdateResponse={onUpdateResponse}
                response={response}
                checkout={checkout}
                responseValidation={responseValidation}
                showValidation={showValidation}
                setClosed={onSimulationClose}
                parentRef={parentRef}
              />
            )}
          </div>
        </QuestionFrame>
      )

    if ('chewyw' == schema.current.store)
      return (
        <QuestionFrame model={model} fullsize={true} images={imageRef.current} noTitle={true}>
          <div ref={parentRef}>
            {closed ? (
              <ClosedShelfView
                schema={schema.current}
                response={response}
                store="Chewy"
                onAction={onSimulationOpen}
                onUpdateResponse={onUpdateResponse}
              />
            ) : (
              <ChewyView
                schema={schema.current}
                onUpdateResponse={onUpdateResponse}
                response={response}
                checkout={checkout}
                responseValidation={responseValidation}
                showValidation={showValidation}
                setClosed={onSimulationClose}
                parentRef={parentRef}
              />
            )}
          </div>
        </QuestionFrame>
      )

    if ('krogerw' == schema.current.store)
      return (
        <QuestionFrame model={model} fullsize={true} images={imageRef.current} noTitle={true}>
          <div ref={parentRef}>
            {closed ? (
              <ClosedShelfView
                schema={schema.current}
                response={response}
                store="Kroger"
                onAction={onSimulationOpen}
                onUpdateResponse={onUpdateResponse}
              />
            ) : (
              <KrogerView
                schema={schema.current}
                onUpdateResponse={onUpdateResponse}
                response={response}
                checkout={checkout}
                responseValidation={responseValidation}
                showValidation={showValidation}
                setClosed={onSimulationClose}
                parentRef={parentRef}
              />
            )}
          </div>
        </QuestionFrame>
      )

    if ('samsclubw' == schema.current.store)
      return (
        <QuestionFrame model={model} fullsize={true} images={imageRef.current} noTitle={true}>
          <div ref={parentRef}>
            {closed ? (
              <ClosedShelfView
                schema={schema.current}
                response={response}
                store="Sam's Club"
                onAction={onSimulationOpen}
                onUpdateResponse={onUpdateResponse}
              />
            ) : (
              <SamsClubView
                schema={schema.current}
                onUpdateResponse={onUpdateResponse}
                response={response}
                checkout={checkout}
                responseValidation={responseValidation}
                showValidation={showValidation}
                setClosed={onSimulationClose}
                parentRef={parentRef}
              />
            )}
          </div>
        </QuestionFrame>
      )

    return (
      <QuestionFrame model={model} fullsize={true}>
        <OldEcommerceQuestion
          questionSchema={immutableModelSchema}
          answerChanged={onUpdateOldResponse}
          onWidthChanged={changeWidth}
          onLogEvent={logEvent}
          theme={layoutIsCompact(session.props.layout) ? 'mobile' : viewport.device}
          noHeader={true}
          width={width}
          preview={session.action.preview != null}
          previewMode={session.action.mode}
          emulatedMobile={viewport.emulationMobileFrame || layoutIsCompact(session.props.layout)}
          accentColor={theme.style.accentColor}
          showErrors={showValidation}
          goNext={doNext}
          inApp={session.style?.flutter}
          locale={localeID}
          innerWidth={viewport.width}
          questionTitle={questionTitle}
        />
      </QuestionFrame>
    )
  }),
  (prevProps, nextProps) => prevProps.model === nextProps.model
)

EcommerceQuestion.displayName = 'EcommerceQuestion'

export default EcommerceQuestion
